import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVolver } from '@/shared/hooks/useVolver';
import { mealsApi, productsApi, type CreateMealInput } from '@/shared/api/endpoints';
import { NetworkError } from '@/shared/api/httpClient';
import type { MealType, MeasurementUnit, Product } from '@/shared/api/types';
import { mealTypeLabel, unitLabel } from '@/shared/format/nutrition';
import { describeError } from '@/shared/hooks/useAsync';
import { mealQueue } from '@/shared/offline/queue';
import { Banner, Button, Card, Eyebrow } from '@/shared/ui';
import { BackIcon, CheckIcon, CloseIcon, GalleryIcon } from '@/shared/ui/icons';

/**
 * Una línea de la carga rápida: un alimento, con su cantidad y su sugerencia del catálogo.
 */
interface QuickLine {
  id: string;
  text: string;
  quantity: string;
  unit: MeasurementUnit;
  /** El mejor candidato del catálogo para lo que se escribió. Null mientras no haya. */
  suggestion: Product | null;
  /** La persona descartó la sugerencia: no se vuelve a ofrecer hasta que cambie el texto. */
  dismissed: boolean;
}

interface QuickMeal {
  id: string;
  type: MealType;
  lines: QuickLine[];
  photos: File[];
  previews: string[];
  /** Ya se registró en el servidor: un reintento tras un error parcial no la duplica. */
  saved: boolean;
}

/**
 * La hora típica de cada comida, para un registro retrospectivo.
 *
 * RF-07 rechaza comidas en el futuro, así que la hora típica se recorta a «ahora» cuando todavía
 * no pasó: cargar el día a las 11 no puede fechar la cena a las 21:30.
 */
const TYPICAL_HOUR: Record<MealType, [number, number]> = {
  Breakfast: [8, 30],
  Lunch: [13, 0],
  AfternoonSnack: [17, 0],
  Dinner: [21, 30],
  Snack: [11, 0],
};

const SUGGESTION_DEBOUNCE_MS = 450;

/** Menos que esto no identifica nada: «po» sugiere cualquier cosa. */
const SUGGESTION_MIN_CHARS = 3;

let nextId = 0;
const freshId = () => `qd-${++nextId}`;

const emptyLine = (): QuickLine => ({
  id: freshId(),
  text: '',
  quantity: '',
  unit: 'Gram',
  suggestion: null,
  dismissed: false,
});

const emptyMeal = (type: MealType): QuickMeal => ({
  id: freshId(),
  type,
  lines: [emptyLine()],
  photos: [],
  previews: [],
  saved: false,
});

/**
 * S-11 · la carga rápida del día completo.
 *
 * CU-02 siempre admitió comidas con varios ítems y la API lo sirve desde el primer día; lo que no
 * existía era una pantalla que lo aprovechara — registrar un almuerzo de tres cosas eran tres
 * pasadas por el formulario. Acá el día entero se declara de una vez: una tarjeta por comida, un
 * alimento por línea, y las colaciones se agregan a demanda.
 *
 * Las sugerencias respetan la línea de RN-03: el sistema nunca elige por debajo de 0,75, pero la
 * persona puede elegir lo que quiera (CU-12). Aceptar una sugerencia toma el nombre exacto del
 * catálogo — con eso la coincidencia difusa es total y la cascada resuelve sola, con fuente
 * «catálogo» y su confianza real. No se inventa una confirmación que el motor no dio.
 */
export function QuickDayPage() {
  const navigate = useNavigate();
  const volver = useVolver('/');
  const [meals, setMeals] = useState<QuickMeal[]>([
    emptyMeal('Breakfast'),
    emptyMeal('Lunch'),
    emptyMeal('AfternoonSnack'),
    emptyMeal('Dinner'),
  ]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [queuedCount, setQueuedCount] = useState(0);

  useEffect(
    () => () => {
      meals.forEach((meal) => meal.previews.forEach((url) => URL.revokeObjectURL(url)));
    },
    // Sólo al desmontar: revocar en cada render mataría las previsualizaciones vivas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const patchMeal = (mealId: string, patch: (meal: QuickMeal) => QuickMeal) => {
    setMeals((current) => current.map((meal) => (meal.id === mealId ? patch(meal) : meal)));
  };

  const patchLine = (mealId: string, lineId: string, patch: (line: QuickLine) => QuickLine) => {
    patchMeal(mealId, (meal) => ({
      ...meal,
      lines: meal.lines.map((line) => (line.id === lineId ? patch(line) : line)),
    }));
  };

  const addPhotos = (mealId: string, files: File[]) => {
    patchMeal(mealId, (meal) => ({
      ...meal,
      photos: [...meal.photos, ...files],
      previews: [...meal.previews, ...files.map((file) => URL.createObjectURL(file))],
    }));
  };

  const removePhoto = (mealId: string, index: number) => {
    patchMeal(mealId, (meal) => {
      const url = meal.previews[index];
      if (url !== undefined) {
        URL.revokeObjectURL(url);
      }
      return {
        ...meal,
        photos: meal.photos.filter((_, position) => position !== index),
        previews: meal.previews.filter((_, position) => position !== index),
      };
    });
  };

  const hasContent = (meal: QuickMeal) =>
    meal.photos.length > 0 || meal.lines.some((line) => line.text.trim() !== '');

  const anyContent = meals.some(hasContent);

  /**
   * Registra cada comida con contenido, en orden.
   *
   * Una comida por request y no todas juntas: la API crea comidas de a una, y así un fallo en la
   * tercera no pierde a las dos primeras — quedan marcadas `saved` y el reintento sigue desde ahí.
   */
  const submit = async () => {
    setSubmitting(true);
    setError(null);
    let queued = queuedCount;

    for (const meal of meals) {
      if (meal.saved || !hasContent(meal)) {
        continue;
      }

      const declaredLines = meal.lines.filter((line) => line.text.trim() !== '');

      const items: CreateMealInput['items'] = declaredLines.map((line) => {
        const quantity = line.quantity === '' ? null : Number(line.quantity.replace(',', '.'));
        return {
          freeText: line.text.trim(),
          ean: null,
          quantity,
          unit: quantity === null ? null : line.unit,
        };
      });

      // Una comida que es sólo fotos lleva un ítem sin declaración: es el caso «foto sola» y el
      // resolutor de visión existe para eso (RN-21).
      if (items.length === 0) {
        items.push({ freeText: null, ean: null, quantity: null, unit: null });
      }

      const payload: CreateMealInput = {
        occurredAt: occurredAtFor(meal.type),
        type: meal.type,
        note: null,
        items,
      };

      try {
        const created = await mealsApi.create(payload);

        /*
         * Las fotos de la comida van al primer ítem, en orden. El modelo de datos adjunta
         * imágenes por ítem (RF-10); repartirlas a ciegas entre líneas les asignaría fotos a
         * alimentos que no son. En el primer ítem quedan encontrables y la visión las usa si la
         * cascada llega hasta ahí.
         */
        const target = created.items[0];
        if (target !== undefined) {
          for (const photo of meal.photos) {
            await mealsApi.attachImage(created.id, target.id, photo, 'Dish');
          }
        }

        patchMeal(meal.id, (current) => ({ ...current, saved: true }));
      } catch (cause) {
        if (cause instanceof NetworkError) {
          // RF-12 · sin red la declaración se encola; las fotos no viajan (mismo límite que el
          // registro simple) y se avisa al final.
          mealQueue.enqueue(payload);
          queued += 1;
          patchMeal(meal.id, (current) => ({ ...current, saved: true }));
          continue;
        }

        setError(describeError(cause));
        setSubmitting(false);
        setQueuedCount(queued);
        return;
      }
    }

    setQueuedCount(queued);
    navigate('/', { replace: true });
  };

  return (
    <>
      <header className="row-between">
        <button
          type="button"
          className="btn-square"
          aria-label="Volver"
          onClick={volver}
        >
          <BackIcon size={18} />
        </button>
        <h1 style={{ fontSize: 'var(--text-h2)' }}>Cargar el día</h1>
        <span style={{ width: 38 }} />
      </header>

      <p className="micro muted">
        Un alimento por línea. Si aparece el del catálogo, usalo y la estimación sale de ahí; si
        no, con la descripción alcanza.
      </p>

      {error !== null && <Banner tone="danger">{error}</Banner>}
      {queuedCount > 0 && (
        <Banner tone="info">
          {queuedCount === 1 ? 'Una comida quedó encolada' : `${queuedCount} comidas quedaron encoladas`}{' '}
          sin conexión. Se envían solas cuando vuelva la señal.
        </Banner>
      )}

      {meals.map((meal) => (
        <QuickMealCard
          key={meal.id}
          meal={meal}
          onLineChange={(lineId, patch) => patchLine(meal.id, lineId, patch)}
          onAddLine={() => patchMeal(meal.id, (m) => ({ ...m, lines: [...m.lines, emptyLine()] }))}
          onRemoveLine={(lineId) =>
            patchMeal(meal.id, (m) => ({
              ...m,
              lines:
                m.lines.length === 1 ? [emptyLine()] : m.lines.filter((line) => line.id !== lineId),
            }))
          }
          onAddPhotos={(files) => addPhotos(meal.id, files)}
          onRemovePhoto={(index) => removePhoto(meal.id, index)}
        />
      ))}

      {/* Las colaciones no tienen tope: RN-19 las propone a cualquier hora y RF-05 no las limita. */}
      <Button
        variant="quiet"
        block
        onClick={() => setMeals((current) => [...current, emptyMeal('Snack')])}
      >
        + Agregar una colación
      </Button>

      <div className="sticky-foot">
        <Button
          variant="primary"
          block
          disabled={submitting || !anyContent}
          onClick={() => void submit()}
        >
          {submitting ? 'Registrando…' : 'Registrar el día'}
        </Button>
        {!anyContent && (
          <p className="micro muted" role="status">
            Anotá al menos un alimento o sumale una foto a alguna comida.
          </p>
        )}
      </div>
    </>
  );
}

/** La hora típica del tipo, hoy, recortada a ahora para no caer en RF-07. */
function occurredAtFor(type: MealType): string {
  const [hour, minute] = TYPICAL_HOUR[type];
  const candidate = new Date();
  candidate.setHours(hour, minute, 0, 0);

  const now = new Date();

  return (candidate > now ? now : candidate).toISOString();
}

function QuickMealCard({
  meal,
  onLineChange,
  onAddLine,
  onRemoveLine,
  onAddPhotos,
  onRemovePhoto,
}: {
  meal: QuickMeal;
  onLineChange: (lineId: string, patch: (line: QuickLine) => QuickLine) => void;
  onAddLine: () => void;
  onRemoveLine: (lineId: string) => void;
  onAddPhotos: (files: File[]) => void;
  onRemovePhoto: (index: number) => void;
}) {
  const photoInput = useRef<HTMLInputElement>(null);

  return (
    <Card>
      <div className="row-between">
        <Eyebrow>{mealTypeLabel(meal.type)}</Eyebrow>
        <button
          type="button"
          className="btn-square"
          aria-label={`Agregar fotos a ${mealTypeLabel(meal.type).toLowerCase()}`}
          onClick={() => photoInput.current?.click()}
        >
          <GalleryIcon size={16} />
        </button>
      </div>

      {meal.lines.map((line) => (
        <QuickLineRow
          key={line.id}
          line={line}
          onChange={(patch) => onLineChange(line.id, patch)}
          onRemove={() => onRemoveLine(line.id)}
        />
      ))}

      <Button variant="plain" onClick={onAddLine} style={{ alignSelf: 'flex-start' }}>
        + Otro alimento
      </Button>

      {meal.previews.length > 0 && (
        <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {meal.previews.map((url, index) => (
            <div key={url} style={{ position: 'relative' }}>
              <img
                src={url}
                alt={`Foto ${index + 1} de ${mealTypeLabel(meal.type).toLowerCase()}`}
                style={{
                  width: 54,
                  height: 54,
                  borderRadius: 'var(--radius-control)',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              <button
                type="button"
                className="thumb-remove"
                aria-label={`Quitar la foto ${index + 1}`}
                onClick={() => onRemovePhoto(index)}
              >
                <CloseIcon size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        ref={photoInput}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          event.target.value = '';
          if (files.length > 0) {
            onAddPhotos(files);
          }
        }}
      />
    </Card>
  );
}

/**
 * Una línea: descripción, sugerencia y — recién cuando hay texto — cantidad y unidad.
 *
 * La cantidad no aparece antes porque la mayoría de las líneas no la van a necesitar: «una
 * estimación sirve», y pedir cuatro controles por línea convertiría la carga rápida en la lenta.
 */
function QuickLineRow({
  line,
  onChange,
  onRemove,
}: {
  line: QuickLine;
  onChange: (patch: (line: QuickLine) => QuickLine) => void;
  onRemove: () => void;
}) {
  const [searching, setSearching] = useState(false);

  const text = line.text;
  const dismissed = line.dismissed;
  const accepted = line.suggestion !== null && line.suggestion.name === text;

  useEffect(() => {
    const trimmed = text.trim();

    if (trimmed.length < SUGGESTION_MIN_CHARS || dismissed || accepted) {
      return;
    }

    let active = true;
    setSearching(true);

    const timer = window.setTimeout(() => {
      productsApi
        .search(trimmed, 1)
        .then((result) => {
          if (!active) {
            return;
          }
          const top = result.matches[0]?.product ?? null;
          // Una sugerencia idéntica a lo escrito no informa nada.
          onChange((current) => ({
            ...current,
            suggestion: top !== null && top.name !== current.text ? top : null,
          }));
        })
        .catch(() => {
          // Sin catálogo no hay sugerencia y la línea sigue sirviendo: la descripción sola ya es
          // una declaración válida (RF-09).
        })
        .finally(() => {
          if (active) {
            setSearching(false);
          }
        });
    }, SUGGESTION_DEBOUNCE_MS);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
    // `onChange` es estable por construcción (setState funcional); re-suscribir por él re-buscaría
    // en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, dismissed, accepted]);

  const accept = (product: Product) => {
    onChange((current) => ({
      ...current,
      text: product.name,
      // El peso de la porción precarga la cantidad sólo si no había una: lo que la persona
      // escribió vale más que el envase.
      quantity:
        current.quantity === '' && product.servingWeightG !== null
          ? String(product.servingWeightG)
          : current.quantity,
      unit: current.quantity === '' && product.servingWeightG !== null ? 'Gram' : current.unit,
      suggestion: product,
    }));
  };

  return (
    <div className="stack" style={{ gap: 'var(--space-1)' }}>
      <div className="row" style={{ gap: 'var(--space-2)' }}>
        <div className="field" style={{ flex: 1 }}>
          <input
            aria-label="Alimento"
            placeholder="Milanesa de pollo, ensalada…"
            value={line.text}
            onChange={(event) =>
              onChange((current) => ({
                ...current,
                text: event.target.value,
                suggestion: null,
                dismissed: false,
              }))
            }
          />
        </div>
        <button type="button" className="btn-square" aria-label="Quitar la línea" onClick={onRemove}>
          <CloseIcon size={14} />
        </button>
      </div>

      {line.suggestion !== null && !accepted && (
        <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
          <button
            type="button"
            className="chip"
            onClick={() => accept(line.suggestion!)}
            aria-label={`Usar ${line.suggestion.name} del catálogo`}
          >
            <CheckIcon size={12} />
            {line.suggestion.name}
            {line.suggestion.servingWeightG !== null &&
              ` · porción ${line.suggestion.servingWeightG} g`}
          </button>
          <button
            type="button"
            className="btn-square"
            aria-label="Descartar la sugerencia"
            style={{ width: 30, height: 30, minHeight: 30 }}
            onClick={() => onChange((current) => ({ ...current, dismissed: true }))}
          >
            <CloseIcon size={12} />
          </button>
        </div>
      )}

      {accepted && (
        <span className="nano muted">Del catálogo: la estimación sale de sus valores.</span>
      )}
      {searching && <span className="nano muted">Buscando en el catálogo…</span>}

      {line.text.trim() !== '' && (
        <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
          <div className="field" style={{ width: 90 }}>
            <input
              aria-label="Cantidad"
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              placeholder="Cant."
              value={line.quantity}
              onChange={(event) =>
                onChange((current) => ({ ...current, quantity: event.target.value }))
              }
            />
          </div>
          <div className="segmented" role="group" aria-label="Unidad" style={{ flex: 1 }}>
            {(['Gram', 'Milliliter', 'Unit', 'Serving'] as MeasurementUnit[]).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={line.unit === option}
                onClick={() => onChange((current) => ({ ...current, unit: option }))}
              >
                {unitLabel(option)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
